import { ChangeEvent, DragEvent, useEffect, useRef, useState } from "react";
import { DocumentMinimalData, DocumentType } from "../../../../types";
import { enqueueSnackbar } from "../../../../components/GlobalSnackbar/snackbarStore";
import useGetDriveConfig from "../../../google-drive/api/useGetDriveConfig";
import { useGetDocumentTypes } from "../../api/useGetDocumentTypes";

function getDefaultDocumentType(file: File): DocumentType {
  if (file.type.startsWith('image/')) return 'Photos';
  if (file.type === 'application/pdf') return 'Devis';
  if (file.type.startsWith('video/')) return 'Autre';
  return 'Autre';
}

type Props = {
  documents: DocumentMinimalData[];
  onChange: (documents: DocumentMinimalData[]) => void;
  defaultDocumentType?: DocumentType;
  hideType?: boolean;
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export default function useDocumentsFormSection(props: Props) {
  const [isDragOver, setIsDragOver] = useState(false);
  // Several field callbacks can fire in the same tick (e.g. provider id then provider name):
  // each update builds on the previous one instead of the stale props snapshot.
  const latestDocuments = useRef(props.documents);
  latestDocuments.current = props.documents;
  const { data: driveConfig, isLoading: isDriveLoading } = useGetDriveConfig();
  const { data: documentTypes } = useGetDocumentTypes();

  // If hideType is true and defaultDocumentType is set, keep all documents forced to that type
  useEffect(() => {
    if (!props.hideType || !props.defaultDocumentType || !documentTypes || documentTypes.length === 0) return;
    const targetTypeName = props.defaultDocumentType;
    const matched = documentTypes.find(
      (dt) => dt.name.toLowerCase() === targetTypeName.toLowerCase()
    );

    let hasChanges = false;
    const updated = props.documents.map((doc) => {
      const targetId = matched?.id ?? doc.document_type_id ?? null;
      const targetName = matched?.name ?? targetTypeName;
      if (doc.type !== targetName || (matched && doc.document_type_id !== targetId)) {
        hasChanges = true;
        return {
          ...doc,
          type: targetName,
          document_type_id: targetId,
        };
      }
      return doc;
    });

    if (hasChanges) {
      props.onChange(updated);
    }
  }, [props.hideType, props.defaultDocumentType, documentTypes, props.documents, props.onChange]);

  // Treat loading as "connected" to avoid flicker; blocks only when the
  // space has confirmed no config.
  const canUploadFiles = isDriveLoading || !!driveConfig;

  const addFiles = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray.filter((file) => {
      if (file.size > MAX_FILE_SIZE) {
        enqueueSnackbar(
          `Le fichier "${file.name}" est trop volumineux (max 10Mo).`,
          "error"
        );
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    const newDocuments: DocumentMinimalData[] = validFiles.map((file) => {
      const typeName = props.defaultDocumentType ?? getDefaultDocumentType(file);
      const matched = documentTypes?.find((dt) => dt.name.toLowerCase() === typeName.toLowerCase())
        ?? documentTypes?.find((dt) => dt.name === 'Autre')
        ?? documentTypes?.[0];

      return {
        id: crypto.randomUUID(),
        name: file.name.replace(/\.[^.]+$/, ""),
        file_url: null,
        document_type_id: matched?.id ?? null,
        type: matched?.name ?? typeName,
        pendingFile: file,
      };
    });
    props.onChange([...props.documents, ...newDocuments]);
  };

  const addLink = () => {
    const defaultTypeName = props.defaultDocumentType ?? "Autre";
    const matched = documentTypes?.find((dt) => dt.name.toLowerCase() === defaultTypeName.toLowerCase())
      ?? documentTypes?.find((dt) => dt.name === 'Autre')
      ?? documentTypes?.[0];

    const newLink: DocumentMinimalData = {
      id: crypto.randomUUID(),
      name: "",
      file_url: "",
      document_type_id: matched?.id ?? null,
      type: matched?.name ?? defaultTypeName,
      isLink: true,
    };
    props.onChange([...props.documents, newLink]);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length > 0 && canUploadFiles) {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    if (canUploadFiles) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
    }
    e.target.value = '';
  };

  const updateDocument = (
    id: string,
    updates: Partial<DocumentMinimalData>,
  ) => {
    const updated = latestDocuments.current.map((doc) => (doc.id === id ? { ...doc, ...updates } : doc));
    latestDocuments.current = updated;
    props.onChange(updated);
  };

  const deleteDocument = (id: string) => {
    props.onChange(props.documents.filter((doc) => doc.id !== id));
  };

  return {
    isDragOver,
    canUploadFiles,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleFileInputChange,
    updateDocument,
    deleteDocument,
    addLink,
    documentTypes: documentTypes ?? [],
  };
}
